import React, { useCallback, useMemo, useState } from "react";
import { Button, Card, Form, Row, Col } from "react-bootstrap";
import { Download } from "lucide-react";
import UploadFile from "../UploadFile";
import { DisplayBulkUploadedQnA } from "./DisplayBulkUploadedQnA";
import { CustomInput } from "components/custom-form-input/CustomInputs";
import { getSignedUrl } from "ServerAPI/serverAPI";
import { useToast } from "context/toast-context/ToastProvider";
import { useCookies } from "react-cookie";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import dayjs from "dayjs";
import { HelpCircle } from "react-feather";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";

const QuizBulkUpload = ({
  handleQuizBulkUpload,
  quizGenerationResponseData,
  handleGeneratedQuizSubmit,
  scheduledAttempt,
  setScheduledAttempt,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  effectiveCourseId,
  activeSection,
  quizTimer,
  setQuizTimer,
  passingPercentage,
  setPassingPercentage,
  finalAssessment,
  setFinalAssessment,
  isProctored,
  setIsProctored,
  maxAttempts,
  setMaxAttempts,
  allowMultipleAttempts,
  setAllowMultipleAttempts,
  contentTitle,
}) => {
  const { showToast } = useToast();
  const [cookies, setCookies] = useCookies();
  const [fileInfo, setFileInfo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [passingPercentageError, setPassingPercentageError] = useState(null);

  const handleFileChange = (e) => {
    setFileInfo(e.target.files[0]);
  };

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1]; // This removes the prefix
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  const downloadTemplate = async () => {
    setIsDownloadingTemplate(true);

    try {
      const payload = {
        method: "GET",
        downloadTemplate: true,
        fileName: "Bulk_Upload_Format.pdf",
        userId: cookies?.userId,
      };

      const response = await getSignedUrl(payload);

      if (response.status === 200 && response.data?.preSignedUrl) {
        // Open the presigned URL in a new tab
        window.open(response.data.preSignedUrl, '_blank');

        showToast("Template downloaded", "success");
      } else {
        showToast(response?.data?.message || "Failed to get download URL", "error");
      }
    } catch (error) {
      console.error("Template download error:", error);
      showToast(
        error?.response?.data?.message || "Failed to download template. Please try again.",
        "error"
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const callQuizBulkUpload = async () => {
    if (!contentTitle || contentTitle.trim() === "") {
      showToast("Content title is mandatory", "error");
      return;
    }

    if (passingPercentage > 100) {
      setPassingPercentageError("Passing percentage should be less than 100");
      showToast("Passing percentage should be less than 100", "error");
      return;
    } else {
      setPassingPercentageError(null);
    }

    setIsGenerating(true);

    try {
      const base64File = await fileToBase64(fileInfo);
      const payload = {
        file: base64File,
      };

      const response = await handleQuizBulkUpload(payload);
      console.log("response", response);

      if (response.status !== 200) {
        showToast(response?.response?.data?.message || "Upload failed", "error");
      } else {
        showToast("Quiz uploaded successfully!", "success");
      }
    } catch (error) {
      console.error("Quiz upload error:", error);
      showToast(error?.response?.data?.message || "Upload failed. Please try again.", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOnChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === "quizTimer") {
      setQuizTimer(value);
    } else if (name === "passingPercentage") {
      setPassingPercentage(value);
    } else if (name === "finalAssessment") {
      setFinalAssessment(checked);
    } else if (name === "maxAttempts") {
      setMaxAttempts(value);
    }
  };

  const shouldDisableBtn = useMemo(() => {
    if (!fileInfo) {
      return true;
    }
    if (isGenerating) {
      return true;
    }
    return false;
  }, [fileInfo, isGenerating]);

  return (
    <>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4 border-bottom py-2">
            <h4 className="mb-0">Quiz Bulk Upload</h4>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={downloadTemplate}
              disabled={isDownloadingTemplate}
              className="d-flex align-items-center gap-2"
            >
              <Download size={16} />
              {isDownloadingTemplate ? "Downloading..." : "Download Template"}
            </Button>
          </div>

          <Row className="g-3">
            {/* Passing Percentage */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium">Passing Percentage</label>
                <CustomInput
                  type="number"
                  id="passing-percentage"
                  label=""
                  placeholder="Enter passing percentage"
                  className="form-control"
                  name="passingPercentage"
                  onChange={handleOnChange}
                  value={passingPercentage}
                  min="0"
                  max="100"
                />
                {passingPercentageError && (
                  <div className="text-danger mt-1 small">{passingPercentageError}</div>
                )}
              </div>
            </Col>

            {/* Final Assessment */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Final Assessment
                  {allowMultipleAttempts && (
                    <Tippy content="Not applicable when Allow Multiple Attempts is enabled — a final assessment is a one-shot evaluation.">
                      <span className="ms-2 text-muted cursor-pointer">
                        <HelpCircle size={16} />
                      </span>
                    </Tippy>
                  )}
                </label>
                <div className="mt-2">
                  <Form.Check
                    onChange={handleOnChange}
                    name="finalAssessment"
                    label="Mark as final assessment"
                    checked={!allowMultipleAttempts && finalAssessment}
                    disabled={allowMultipleAttempts}
                    id="final-assessment-check"
                  />
                </div>
              </div>
            </Col>

            {/* Quiz Timer */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium">Quiz Timer (Minutes)</label>
                <CustomInput
                  type="number"
                  id="quiz-timer"
                  label=""
                  placeholder="Enter quiz duration"
                  className="form-control"
                  name="quizTimer"
                  onChange={handleOnChange}
                  value={quizTimer}
                  min="1"
                />
              </div>
            </Col>

            {/* Allow Multiple Attempts */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Allow Multiple Attempts
                  <Tippy content={finalAssessment ? "Not applicable when Final Assessment is enabled." : "Not applicable when Scheduled or Proctored Quiz is enabled."}>
                    <span className="ms-2 text-muted cursor-pointer">
                      <HelpCircle size={16} />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="allow-multiple-attempts-switch"
                    checked={(scheduledAttempt || isProctored || finalAssessment) ? false : allowMultipleAttempts}
                    onChange={(e) => setAllowMultipleAttempts(e.target.checked)}
                    disabled={scheduledAttempt || isProctored || finalAssessment}
                    label={(scheduledAttempt || isProctored || finalAssessment) || !allowMultipleAttempts ? "No Attempt Tracking" : "Multiple Attempts"}
                  />
                </div>
              </div>
            </Col>

            {allowMultipleAttempts && !(scheduledAttempt || isProctored || finalAssessment) && (
              <Col xs={12} md={6} lg={4}>
                <div className="mb-3">
                  <label className="form-label fw-medium">Max Attempts</label>
                  <CustomInput
                    type="number"
                    id="max-attempts"
                    label=""
                    placeholder="Enter max attempts allowed"
                    className="form-control"
                    name="maxAttempts"
                    onChange={handleOnChange}
                    value={maxAttempts}
                    min="2"
                  />
                </div>
              </Col>
            )}

            {/* Proctoring Toggle */}
            <Col xs={12} md={6} lg={4}>
              <div className="mb-3">
                <label className="form-label fw-medium d-flex align-items-center">
                  Proctored Quiz
                  <Tippy content="Enabling proctoring will apply Fullscreen Enforcement, Tab/Window Switch Detection, Copy-Paste & Right-Click Disable, and DevTools/Shortcut Blocking when attempting this quiz.">
                    <span className="ms-2 text-muted cursor-pointer">
                      <HelpCircle size={16} />
                    </span>
                  </Tippy>
                </label>
                <div className="mt-2">
                  <Form.Check
                    type="switch"
                    id="bulk-proctoring-switch"
                    checked={isProctored}
                    onChange={(e) => setIsProctored(e.target.checked)}
                    label={isProctored ? "Proctoring Enabled" : "Proctoring Disabled"}
                  />
                </div>
              </div>
            </Col>

            {/* Scheduling Section */}
            <Col xs={12}>
              <div className="p-3 border rounded bg-light mb-3">
                <div className="d-flex align-items-center mb-3">
                  <Form.Check
                    type="switch"
                    id="bulk-scheduled-quiz-switch"
                    label={<span className="fw-bold fs-5 ms-2">Scheduled Quiz</span>}
                    checked={scheduledAttempt}
                    onChange={(e) => setScheduledAttempt(e.target.checked)}
                  />
                  <Tippy content="If enabled, the quiz will only be accessible during the specified date and time window.">
                    <span className="ms-2 text-muted cursor-pointer">
                      <HelpCircle size={16} />
                    </span>
                  </Tippy>
                </div>

                {scheduledAttempt && (
                  <Row className="g-3 animate__animated animate__fadeIn">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small text-uppercase fw-bold text-muted">Availability Starts</Form.Label>
                        <div className="d-flex gap-2">
                          <Flatpickr
                            value={startDate}
                            onChange={([date]) => setStartDate(dayjs(date).format("YYYY-MM-DD"))}
                            options={{ minDate: "today", dateFormat: "Y-m-d" }}
                            className="form-control"
                            placeholder="Select Date"
                          />
                          <MobileTimePicker
                            value={startTime ? dayjs(startTime, "HH:mm") : null}
                            onChange={(newValue) => {
                              setStartTime(newValue ? newValue.format("HH:mm") : "");
                            }}
                            views={["hours", "minutes"]}
                            openTo="hours"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                className: "bg-white",
                                sx: {
                                  "& .MuiInputBase-root": {
                                    height: "38px",
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="small text-uppercase fw-bold text-muted">Availability Ends</Form.Label>
                        <div className="d-flex gap-2">
                          <Flatpickr
                            value={endDate}
                            onChange={([date]) => setEndDate(dayjs(date).format("YYYY-MM-DD"))}
                            options={{ minDate: startDate || "today", dateFormat: "Y-m-d" }}
                            className="form-control"
                            placeholder="Select Date"
                          />
                          <MobileTimePicker
                            value={endTime ? dayjs(endTime, "HH:mm") : null}
                            onChange={(newValue) => {
                              setEndTime(newValue ? newValue.format("HH:mm") : "");
                            }}
                            views={["hours", "minutes"]}
                            openTo="hours"
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                className: "bg-white",
                                sx: {
                                  "& .MuiInputBase-root": {
                                    height: "38px",
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </div>
            </Col>

            {/* File Upload */}
            <Col xs={12}>
              <div className="mb-3">
                <label className="form-label fw-medium">Upload Quiz File</label>
                <UploadFile
                  fileType="pdf"
                  handleFileChange={handleFileChange}
                  showFileInfo={true}
                  selectedFile={fileInfo}
                  disabled={isGenerating}
                />
              </div>
            </Col>

            {/* Submit Button */}
            <Col xs={12}>
              <div className="d-flex justify-content-end">
                <Button
                  disabled={shouldDisableBtn}
                  onClick={callQuizBulkUpload}
                >
                  {isGenerating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    "Upload Quiz"
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {quizGenerationResponseData?.length > 0 && (
        <Card className="mt-4">
          <Card.Body>
            <DisplayBulkUploadedQnA
              responseData={quizGenerationResponseData}
              handleGeneratedQuizSubmit={handleGeneratedQuizSubmit}
              passingPerctage={passingPercentage}
              finalAssessment={finalAssessment}
              quizTimer={Number(quizTimer)}
              isBulkQuizUpload={true}
              effectiveCourseId={effectiveCourseId}
              activeSection={activeSection}
            />
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default QuizBulkUpload;